import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Space, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { ActionsDropdown } from '@/components/actions-dropdown';
import { API } from '@/services/api.service';
import { useNotify } from '@/hooks';

const StatementDeleteModal = lazy(() => import('../actions/StatementDeleteModal'));

export default function StatementsActions({ record, type }) {
  const { t } = useTranslation(['tables', 'forms', 'messages', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });
  const queryClient = useQueryClient();
  const notification = useNotify();

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ statementId, status }) =>
      API.financialStatements.updateStatementStatus(statementId, status),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['financial-statements']);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message || t('updateSuccess', { ns: 'messages' }),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || error.message,
      });
    },
  });

  const handleUpdateStatus = (newStatus) => {
    updateStatusMutation.mutate({ statementId: record._id, status: newStatus });
  };

  const handleDownloadXml = async (formType) => {
    try {
      await API.financialStatements.downloadXml(record._id, formType);
    } catch (error) {
      console.error('Download failed:', error);
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || t('downloadError', { ns: 'messages' }),
      });
    }
  };

  const getStatusMenuItems = () => {
    const items = [];

    if (record.status !== 'GENERATED') {
      items.push({
        key: 'GENERATED',
        label: fs('markAsGenerated'),
        onClick: () => handleUpdateStatus('GENERATED'),
      });
    }

    if (record.status !== 'SUBMITTED') {
      items.push({
        key: 'SUBMITTED',
        label: fs('markAsSubmitted'),
        onClick: () => handleUpdateStatus('SUBMITTED'),
      });
    }

    if (record.status !== 'DRAFT') {
      items.push({
        key: 'DRAFT',
        label: fs('backToDraft'),
        onClick: () => handleUpdateStatus('DRAFT'),
      });
    }

    return items;
  };

  if (type === 'download') {
    return (
      <Space>
        {record.xmlContent?.bilansStanja && (
          <Tooltip title={fs('downloadBilansStanja')}>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadXml('bilansStanja')}
            >
              BS
            </Button>
          </Tooltip>
        )}
        {record.xmlContent?.bilansUspeha && (
          <Tooltip title={fs('downloadBilansUspeha')}>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadXml('bilansUspeha')}
            >
              BU
            </Button>
          </Tooltip>
        )}
        {record.xmlContent?.statistickiIzvestaj && (
          <Tooltip title={fs('downloadStatisticki')}>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadXml('statistickiIzvestaj')}
            >
              SI
            </Button>
          </Tooltip>
        )}
      </Space>
    );
  }

  return (
    <ActionsDropdown>
      <div>
        {getStatusMenuItems().map((item) => (
          <Button
            key={item.key}
            type="text"
            className="btn-default w-full justify-start px-2 hover:!text-zinc-700"
            onClick={item.onClick}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <Suspense fallback={<div className="h-9 w-9 bg-zinc-100" />}>
        <StatementDeleteModal record={record} />
      </Suspense>
    </ActionsDropdown>
  );
}
